import { type FC } from 'react';
import loadable from '@loadable/component';
import { useServerContext } from '@salesforce/pwa-kit-react-sdk/ssr/universal/hooks';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router';
import { Layout } from '../../components/layout/Layout';
import Seo from '../../components/seo';
import { useIsMobile } from '../../hooks/use-is-mobile';
import { isNoIndexPage } from '../../utils/is-no-index-page';
import { isServer } from '../../utils/utils';
import { SearchSkeletons } from './components/skeletons/SearchSkeletons';
import { useSearches } from './hooks/use-search/useSearches';

const Search = loadable(() => import('./Search'));

interface SearchProps {
    location?: Location;
}

export const SearchLazy: FC<SearchProps> = () => {
    const intl = useIntl();
    const location = useLocation();
    const serverContext = useServerContext();
    if (isServer && serverContext) {
        // no cache for search results
        serverContext.res.set('Cache-Control', 'no-cache');
        serverContext.res.setHeader?.(
            'Accept-CH',
            'Sec-CH-Viewport-Width,Sec-CH-Downlink,Sec-CH-UA,Sec-CH-UA-Platform,Sec-CH-UA-Mobile',
        );
    }
    const { totalResults } = useSearches();
    const isMobile = useIsMobile();

    const query = new URLSearchParams(location?.search).get('q');
    let title = intl.formatMessage(
        {
            id: 'search.results.for',
            defaultMessage: 'Search Results For {query}',
        },
        { query },
    );
    if (!totalResults) {
        title = query
            ? intl.formatMessage(
                  {
                      id: 'no.search.results.for',
                      defaultMessage: 'No Search Results for {query}',
                  },
                  { query },
              )
            : intl.formatMessage({
                  id: 'no.search.results',
                  defaultMessage: 'No Search Results for "undefined"',
              });
    }

    return (
        <Layout stickyNavigation={isMobile}>
            <Seo title={title} noIndex={isNoIndexPage(location?.pathname)} />
            <Search fallback={<SearchSkeletons />} />
        </Layout>
    );
};
